import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";
import { credentialsService } from "@/api/credentials";

// Cache for credentials to avoid multiple API calls
let credentialsCache = null;
let cacheTimestamp = null;

/**
 * Fetch company credentials from API
 * Uses cache to avoid multiple requests
 */
const getCompanyCredentials = async () => {
    // Cache valid for 5 minutes
    const CACHE_DURATION = 5 * 60 * 1000;
    const now = Date.now();

    if (credentialsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
        return credentialsCache;
    }

    try {
        const credentials = await credentialsService.getPublicCredentials();
        credentialsCache = credentials;
        cacheTimestamp = now;
        return credentials;
    } catch (error) {
        console.error("Failed to fetch company credentials:", error);
        // Return fallback credentials if API fails
        return {
            companyName: "Tale Platform SAS",
            address: "123 Avenue des Champs-Élysées, 75008 Paris, France",
            phoneNumber: "+33 1 23 45 67 89",
            taxId: "FR12345678901",
            email: "contact@tale-platform.com"
        };
    }
};

/**
 * Generate PDF invoice from invoice data
 * @param {Object} invoice - Invoice data from API
 * @returns {Promise<jsPDF>} PDF document
 */
export const generateInvoicePDF = async (invoice) => {
    // Fetch company credentials
    const credentials = await getCompanyCredentials();

    // Create new PDF document
    const doc = new jsPDF();

    // Set document properties
    doc.setProperties({
        title: `Facture ${invoice.id}`,
        subject: 'Facture Tale Platform',
        author: credentials.companyName || 'Tale Platform',
        keywords: 'facture, invoice, tale',
        creator: 'Tale Platform'
    });

    // Header Section
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURE', 105, 25, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // Company Information from credentials
    doc.text(credentials.companyName || 'Tale Platform SAS', 14, 40);

    if (credentials.address) {
        const addressLines = splitAddressLines(credentials.address, 50);
        addressLines.forEach((line, index) => {
            doc.text(line, 14, 45 + (index * 5));
        });
        let yPos = 45 + (addressLines.length * 5);

        if (credentials.phoneNumber) {
            doc.text(`Tél: ${credentials.phoneNumber}`, 14, yPos);
            yPos += 5;
        }

        if (credentials.email) {
            doc.text(credentials.email, 14, yPos);
            yPos += 5;
        }

        if (credentials.taxId) {
            doc.text(`TVA: ${credentials.taxId}`, 14, yPos);
        }
    } else {
        doc.text('Adresse non renseignée', 14, 45);
        doc.text(`TVA: ${credentials.taxId || 'Non renseigné'}`, 14, 50);
    }

    // Invoice Number and Dates
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Facture #${invoice.id}`, 160, 40, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date d'émission: ${formatDateForPDF(invoice.creationDate)}`, 160, 50, { align: 'right' });

    if (invoice.dueDate) {
        doc.text(`Date d'échéance: ${formatDateForPDF(invoice.dueDate)}`, 160, 55, { align: 'right' });
    }

    // Client Information
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT:', 14, 85);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    if (invoice.company) {
        doc.text(invoice.company.companyName, 14, 92);
        if (invoice.company.address) {
            const clientAddressLines = splitAddressLines(invoice.company.address, 50);
            clientAddressLines.forEach((line, index) => {
                doc.text(line, 14, 97 + (index * 5));
            });
        }
    }

    if (invoice.user) {
        const clientName = `${invoice.user.firstname || ''} ${invoice.user.lastname || ''}`.trim();
        if (clientName) {
            doc.text(clientName, 14, 102);
        }
        if (invoice.user.email) {
            doc.text(invoice.user.email, 14, 107);
        }
        if (invoice.user.phoneNumber) {
            doc.text(invoice.user.phoneNumber, 14, 112);
        }
    }

    // Line separator
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 120, 196, 120);

    // Items Table
    const items = invoice.items || [];
    const tableData = items.map(item => {
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const quantity = parseInt(item.quantity) || 0;
        const discountPercentage = parseFloat(item.discountPercentage) || 0;
        const discountValue = parseFloat(item.discountValue) || 0;
        const subtotal = unitPrice * quantity;

        return [
            item.name || 'Article sans nom',
            quantity.toString(),
            formatCurrencyForPDF(unitPrice),
            `${discountPercentage}%`,
            formatCurrencyForPDF(discountValue),
            formatCurrencyForPDF(subtotal - discountValue)
        ];
    });

    // Calculate totals
    const subtotal = items.reduce((sum, item) => {
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return sum + (unitPrice * quantity);
    }, 0);

    const totalDiscount = items.reduce((sum, item) => {
        return sum + (parseFloat(item.discountValue) || 0);
    }, 0);

    const afterDiscount = subtotal - totalDiscount;
    const taxAmount = parseFloat(invoice.tax) || 0;
    const total = afterDiscount + taxAmount;

    // Add table
    autoTable(doc,{
        startY: 125,
        head: [['Description', 'Qté', 'Prix unitaire', 'Remise %', 'Montant remise', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold'
        },
        columnStyles: {
            0: { cellWidth: 60 }, // Description
            1: { cellWidth: 20, halign: 'center' }, // Quantity
            2: { cellWidth: 30, halign: 'right' }, // Unit Price
            3: { cellWidth: 25, halign: 'center' }, // Discount %
            4: { cellWidth: 30, halign: 'right' }, // Discount Amount
            5: { cellWidth: 25, halign: 'right' } // Total
        },
        margin: { left: 14, right: 14 }
    });

    // Summary Section
    const finalY = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    let yPos = finalY;

    // Subtotal
    doc.text('Sous-total:', 140, yPos);
    doc.text(formatCurrencyForPDF(subtotal), 196, yPos, { align: 'right' });
    yPos += 7;

    // Discount
    if (totalDiscount > 0) {
        doc.text('Remise totale:', 140, yPos);
        doc.text(`-${formatCurrencyForPDF(totalDiscount)}`, 196, yPos, { align: 'right' });
        yPos += 7;

        doc.text('Montant après remise:', 140, yPos);
        doc.text(formatCurrencyForPDF(afterDiscount), 196, yPos, { align: 'right' });
        yPos += 7;
    }

    // Tax
    doc.text(`TVA:`, 140, yPos);
    doc.text(formatCurrencyForPDF(taxAmount), 196, yPos, { align: 'right' });
    yPos += 7;

    // Total
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL TTC:', 140, yPos);
    doc.text(formatCurrencyForPDF(total), 196, yPos, { align: 'right' });

    // Status and Payment Details
    yPos += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Statut:', 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(getStatusText(invoice.status), 30, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Détails de paiement:', 14, yPos);
    doc.setFont('helvetica', 'normal');

    yPos += 7;
    doc.text(`Banque: ${credentials.companyName || 'Tale Bank'}`, 14, yPos);
    yPos += 5;
    doc.text('RIB: FR76 1234 5678 9012 3456 7890 123', 14, yPos);
    yPos += 5;
    doc.text('BIC: ABCD1234567', 14, yPos);
    yPos += 7;
    doc.text('Merci de préciser le numéro de facture dans votre virement.', 14, yPos);

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);

    const footerCompanyName = credentials.companyName || 'Tale Platform SAS';
    doc.text(`${footerCompanyName}`, 105, pageHeight - 20, { align: 'center' });

    if (credentials.address) {
        doc.text(`Adresse: ${credentials.address}`, 105, pageHeight - 15, { align: 'center' });
    }

    if (credentials.taxId) {
        doc.text(`Matricule Fiscale: ${credentials.taxId}`, 105, pageHeight - 10, { align: 'center' });
    }

    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 105, pageHeight - 5, { align: 'center' });

    return doc;
};

/**
 * Split long address into multiple lines
 */
const splitAddressLines = (address, maxLength) => {
    if (!address) return [];

    const words = address.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
        if ((currentLine + word).length <= maxLength) {
            currentLine += (currentLine ? ' ' : '') + word;
        } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
        }
    });

    if (currentLine) lines.push(currentLine);
    return lines;
};

/**
 * Format date for PDF display
 */
const formatDateForPDF = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

/**
 * Format currency for PDF display
 */
const formatCurrencyForPDF = (amount) => {
    return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',

    }).format(amount || 0);
};

/**
 * Get status text in French
 */
const getStatusText = (status) => {
    switch (status) {
        case 'SUBMITTED': return 'Soumise';
        case 'PAID': return 'Payée';
        case 'REJECTED': return 'Rejetée';
        case 'REFUNDED': return 'Remboursée';
        default: return status || 'En attente';
    }
};

/**
 * Download PDF invoice
 * @param {Object} invoice - Invoice data
 * @param {string} fileName - Optional custom file name
 */
export const downloadInvoicePDF = async (invoice, fileName = null) => {
    try {
        const doc = await generateInvoicePDF(invoice);
        const name = fileName || `Facture-${invoice.id}-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(name);
    } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
    }
};

/**
 * Open PDF in new window
 * @param {Object} invoice - Invoice data
 */
export const openInvoicePDF = async (invoice) => {
    try {
        const doc = await generateInvoicePDF(invoice);
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
        // Clean up the URL object after use
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
    } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
    }
};

/**
 * Get PDF as Blob (for email attachments)
 * @param {Object} invoice - Invoice data
 * @returns {Promise<Blob>} PDF blob
 */
export const getInvoicePDFBlob = async (invoice) => {
    try {
        const doc = await generateInvoicePDF(invoice);
        return doc.output('blob');
    } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
    }
};
